import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ReceiptPercentIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Transactions = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/transactions/my`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      verified: {
        color: 'bg-green-100 text-green-800',
        icon: CheckCircleIcon,
        text: 'Verified'
      },
      confirmed: {
        color: 'bg-green-100 text-green-800',
        icon: CheckCircleIcon,
        text: 'Confirmed'
      },
      pending_verification: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: ClockIcon,
        text: 'Verifying'
      },
      pending_merchant: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: ClockIcon,
        text: 'Awaiting Confirmation'
      },
      pending_confirmation: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: ClockIcon,
        text: 'Pending'
      },
      disputed: {
        color: 'bg-red-100 text-red-800',
        icon: XCircleIcon,
        text: 'Disputed'
      },
      cancelled: {
        color: 'bg-gray-100 text-gray-800',
        icon: XCircleIcon,
        text: 'Cancelled'
      }
    };

    const badge = badges[status] || badges.pending_confirmation;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="h-4 w-4" />
        {badge.text}
      </span>
    );
  };

  const TransactionModal = ({ transaction, onClose }) => {
    if (!transaction) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
            <h2 className="text-2xl font-bold text-gray-900">Transaction Details</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <XCircleIcon className="h-7 w-7" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Status */}
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Status</span>
              {getStatusBadge(transaction.status)}
            </div>

            {/* Restaurant Info */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4">
              <h3 className="font-semibold text-gray-900 mb-2">{transaction.restaurant?.name}</h3>
              <p className="text-sm text-gray-600">{transaction.restaurant?.address}</p>
              <p className="text-sm text-gray-600">{transaction.restaurant?.cuisine}</p>
            </div>

            {/* Bill Details */}
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900">Bill Details</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Original Bill:</span>
                  <span className="font-medium">₹{transaction.originalBillAmount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Discount ({transaction.discountPercentage}%):</span>
                  <span className="font-medium">-₹{transaction.discountAmount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-blue-600">
                  <span>Points Used:</span>
                  <span className="font-medium">{transaction.pointsDeducted}</span>
                </div>
                <div className="border-t border-gray-300 pt-2 flex justify-between text-lg">
                  <span className="font-bold text-gray-900">Amount Paid:</span>
                  <span className="font-bold text-primary-600">₹{transaction.finalAmount?.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Payment Screenshot */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Payment Screenshot</h3>
              {transaction.paymentScreenshot ? (
                <img
                  src={transaction.paymentScreenshot}
                  alt="Payment proof"
                  className="w-full rounded-lg border border-gray-300"
                />
              ) : (
                <p className="text-gray-500">No screenshot available</p>
              )}
              {transaction.upiTransactionId && (
                <p className="mt-2 text-sm">
                  <span className="text-gray-600">UPI Transaction ID:</span>{' '}
                  <span className="font-mono font-medium">{transaction.upiTransactionId}</span>
                </p>
              )}
            </div>

            {/* Transaction Info */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Transaction Date:</span>
                <span className="font-medium">{new Date(transaction.createdAt).toLocaleString()}</span>
              </div>
              {transaction.merchantConfirmedAt && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Confirmed At:</span>
                  <span className="font-medium">{new Date(transaction.merchantConfirmedAt).toLocaleString()}</span>
                </div>
              )}
              {transaction.merchantNotes && (
                <div>
                  <span className="text-gray-600">Merchant Notes:</span>
                  <p className="font-medium mt-1">{transaction.merchantNotes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary-100">
                <ReceiptPercentIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">My Transactions</h1>
                <p className="text-gray-600 mt-1">View all your redemption history</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/redemption')}
              className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              New Redemption
            </button>
          </div>
        </div>

        {/* Transactions List */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {transactions.length === 0 ? (
            <div className="p-12 text-center">
              <ReceiptPercentIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No transactions yet</h3>
              <p className="text-gray-600 mb-6">Start redeeming your points at partner restaurants!</p>
              <button
                onClick={() => navigate('/redemption')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                <ReceiptPercentIcon className="h-5 w-5" />
                Make Your First Redemption
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Restaurant
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bill Amount
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Discount
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paid
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Points Used
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((transaction) => (
                    <tr key={transaction._id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{transaction.restaurant?.name}</div>
                        <div className="text-sm text-gray-500">{transaction.restaurant?.cuisine}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        ₹{transaction.originalBillAmount?.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-green-600 font-medium">
                        -₹{transaction.discountAmount?.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        ₹{transaction.finalAmount?.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-blue-600 font-medium">
                        {transaction.pointsDeducted}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(transaction.status)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(transaction.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <button
                          onClick={() => setSelectedTransaction(transaction)}
                          className="text-primary-600 hover:text-primary-800 font-medium"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Stats */}
        {transactions.length > 0 && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow p-6">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Total Redemptions</h3>
              <p className="text-3xl font-bold text-gray-900">{transactions.length}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-6">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Total Saved</h3>
              <p className="text-3xl font-bold text-green-600">
                ₹{transactions.reduce((sum, t) => sum + (t.discountAmount || 0), 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-white rounded-xl shadow p-6">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Points Redeemed</h3>
              <p className="text-3xl font-bold text-blue-600">
                {transactions.reduce((sum, t) => sum + (t.pointsDeducted || 0), 0).toLocaleString()}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedTransaction && (
        <TransactionModal
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
        />
      )}
    </div>
  );
};

export default Transactions;

